import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, GitBranch, GitCommit, Circle } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { db, GoalTemplate } from '../../lib/db';

interface TemplateFormProps {
  userId: string;
  template?: GoalTemplate | null;
  onClose: () => void;
  onSubmit: () => void;
}

interface TaskItem {
  id: string;
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  depends_on_positions?: number[];
  dependency_type?: 'none' | 'sequential' | 'parallel';
  dependency_group?: string;
  offset_from_position?: number;
  offset_days?: number;
  offset_type?: 'none' | 'days_from_start' | 'days_from_completion';
}

export function TemplateForm({ userId, template, onClose, onSubmit }: TemplateFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (template) {
      setTitle(template.title);
      setDescription(template.description || '');
      setCategory(template.category || '');
      loadTemplateTasks();
    }
  }, [template]);

  const loadTemplateTasks = async () => {
    if (!template) return;
    
    try {
      console.log(`Loading tasks for template ${template.id}`);
      const taskTemplates = await db.taskTemplates.findByGoalTemplateId(template.id);
      console.log(`Found ${taskTemplates.length} tasks:`, taskTemplates.map(t => t.title));
      setTasks(taskTemplates.map(t => ({
        id: t.id,
        title: t.title,
        description: t.description || '',
        priority: t.priority,
        depends_on_positions: t.depends_on_positions,
        dependency_type: t.dependency_type,
        offset_from_position: t.offset_from_position,
        offset_days: t.offset_days || 0,
        offset_type: t.offset_type || 'none',
        dependency_group: t.dependency_group
      })));
    } catch (error) {
      console.error('Error loading template tasks:', error);
    }
  };

  const addTask = () => {
    const newTask: TaskItem = {
      id: `temp-${Date.now()}`,
      title: '',
      description: '',
      priority: 'Medium',
      offset_days: 0,
      offset_type: 'none'
    };
    setTasks([...tasks, newTask]);
  };

  const updateTask = (index: number, field: keyof TaskItem, value: any) => {
    const newTasks = [...tasks];
    newTasks[index] = { ...newTasks[index], [field]: value };
    setTasks(newTasks);
  };

  const removeTask = (index: number) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const handleDragEnd = (result: { source?: { index: number }, destination?: { index: number } }) => {
    if (!result.destination) return;
    
    const items = Array.from(tasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setTasks(items);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || tasks.length === 0) return;
    
    setLoading(true);
    try {
      let goalTemplateId: string;
      
      if (template) {
        // Update existing template
        await db.goalTemplates.update(template.id, userId, {
          title,
          description,
          category
        });
        goalTemplateId = template.id;
        
        // Delete existing task templates
        console.log(`Deleting task templates for goal template ${template.id}`);
        const deleteResult = await db.taskTemplates.deleteByGoalTemplateId(template.id, userId);
        console.log(`Delete result: ${deleteResult}`);
        
        // Add a small delay to ensure deletion completes
        await new Promise(resolve => setTimeout(resolve, 100));
      } else {
        // Create new template
        const newTemplate = await db.goalTemplates.create({
          title,
          description,
          category,
          is_system: false,
          user_id: userId
        });
        goalTemplateId = newTemplate.id;
      }
      
      // Create task templates
      console.log(`Creating ${tasks.length} task templates for goal template ${goalTemplateId}`);
      
      // First pass: Create all tasks without dependencies to avoid trigger validation errors
      const createdTasks: Array<{id: string, position: number}> = [];
      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        if (task.title.trim()) {
          console.log(`Creating task ${i + 1}: ${task.title}`);
          const createdTask = await db.taskTemplates.create({
            goal_template_id: goalTemplateId,
            title: task.title.trim(),
            description: task.description.trim(),
            priority: task.priority,
            position: i,
            user_id: userId,
            depends_on_positions: [], // Empty initially
            dependency_type: 'none', // Set to none initially
            dependency_group: task.dependency_group,
            offset_from_position: task.offset_from_position,
            offset_days: task.offset_days || 0,
            offset_type: task.offset_type || 'none'
          });
          createdTasks.push({ id: createdTask.id, position: i });
        }
      }
      
      // Second pass: Update tasks with their dependencies now that all tasks exist
      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        if (task.title.trim() && (task.depends_on_positions?.length > 0 || task.dependency_type !== 'none')) {
          console.log(`Updating dependencies for task ${i + 1}: ${task.title}`);
          const taskToUpdate = createdTasks.find(t => t.position === i);
          if (taskToUpdate) {
            await db.taskTemplates.update(taskToUpdate.id, userId, {
              depends_on_positions: task.depends_on_positions || [],
              dependency_type: task.dependency_type || 'none'
            });
          }
        }
      }
      
      onSubmit();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {template ? 'Edit Template' : 'Create New Template'}
        </h3>
      </div>
      
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Name *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Website Development"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              placeholder="Brief description of this goal template"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Projects, Learning, Health"
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Tasks *
              </label>
              <button
                type="button"
                onClick={addTask}
                className="flex items-center text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Task
              </button>
            </div>
            
            {tasks.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4 border border-dashed border-gray-300 rounded-md">
                Add at least one task to this template
              </p>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="tasks">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-2"
                    >
                      {tasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="bg-gray-50 p-3 rounded-md border border-gray-200"
                            >
                              <div className="flex items-start gap-2">
                                <div
                                  {...provided.dragHandleProps}
                                  className="mt-2 text-gray-400 cursor-move"
                                >
                                  <GripVertical className="w-5 h-5" />
                                </div>
                                <div className="flex-1 space-y-2">
                                  <input
                                    type="text"
                                    value={task.title}
                                    onChange={(e) => updateTask(index, 'title', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Task title"
                                    required
                                  />
                                  <input
                                    type="text"
                                    value={task.description}
                                    onChange={(e) => updateTask(index, 'description', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Task description (optional)"
                                  />
                                  <select
                                    value={task.priority}
                                    onChange={(e) => updateTask(index, 'priority', e.target.value)}
                                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                    <option value="Critical">Critical</option>
                                  </select>
                                  
                                  {/* Dependency Type Selector */}
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-600">Dependency:</label>
                                    <select
                                      value={task.dependency_type || 'none'}
                                      onChange={(e) => updateTask(index, 'dependency_type', e.target.value)}
                                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="none">None</option>
                                      <option value="sequential">Sequential</option>
                                      <option value="parallel">Parallel</option>
                                    </select>
                                  </div>
                                  
                                  {/* Dependency Selector */}
                                  {task.dependency_type && task.dependency_type !== 'none' && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs text-gray-600">
                                        {task.dependency_type === 'sequential' ? 'After:' : 'With:'}
                                      </span>
                                      {tasks.map((t, i) => {
                                        if (i >= index) return null; // Can only depend on previous tasks
                                        const isSelected = task.depends_on_positions?.includes(i) || false;
                                        return (
                                          <button
                                            key={i}
                                            type="button"
                                            onClick={() => {
                                              const currentDeps = task.depends_on_positions || [];
                                              const newDeps = isSelected 
                                                ? currentDeps.filter(p => p !== i)
                                                : [...currentDeps, i];
                                              updateTask(index, 'depends_on_positions', newDeps);
                                            }}
                                            className={`px-2 py-1 text-xs rounded transition-colors ${
                                              isSelected 
                                                ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' 
                                                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                                            }`}
                                          >
                                            Task {i + 1}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Time Delay/Offset Controls */}
                                  <div className="pt-2 border-t border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                      <label className="text-xs text-gray-600">Time Delay:</label>
                                      <select
                                        value={task.offset_type || 'none'}
                                        onChange={(e) => updateTask(index, 'offset_type', e.target.value)}
                                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                      >
                                        <option value="none">No delay</option>
                                        <option value="days_from_completion">Days after previous task completes</option>
                                        <option value="days_from_start">Days after previous task starts</option>
                                      </select>
                                    </div>
                                    
                                    {task.offset_type && task.offset_type !== 'none' && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-600">Wait:</span>
                                        <input
                                          type="number"
                                          min="0"
                                          max="365"
                                          value={task.offset_days || 0}
                                          onChange={(e) => updateTask(index, 'offset_days', parseInt(e.target.value) || 0)}
                                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <span className="text-xs text-gray-600">days</span>
                                        
                                        <span className="text-xs text-gray-600 ml-2">After task:</span>
                                        <select
                                          value={task.offset_from_position || ''}
                                          onChange={(e) => updateTask(index, 'offset_from_position', e.target.value ? parseInt(e.target.value) : undefined)}
                                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                        >
                                          <option value="">Select task</option>
                                          {tasks.map((t, i) => {
                                            if (i >= index) return null; // Can only depend on previous tasks
                                            return (
                                              <option key={i} value={i}>
                                                Task {i + 1}: {t.title || 'Untitled'}
                                              </option>
                                            );
                                          })}
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeTask(index)}
                                  className="mt-2 text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>
      </div>
      
      <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !title.trim() || tasks.length === 0}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
        </button>
      </div>
    </form>
  );
}